import { setUserId } from '@snowplow/browser-tracker'
import { signInWithApple } from './appleSignIn'
import { signInWithGoogle } from './googleSignIn'
import {
  OAuthCancelledError,
  type OAuthProvider,
  type ProviderCredential,
} from './oauthTypes'
import { supabase } from './supabase'

/**
 * Signs the user in (or up) via Apple/Google and finishes the flow on
 * Supabase. The server-side oauth-exchange edge function preserves the
 * ${APP_ID}_<email> prefixing scheme — we do not use
 * supabase.auth.signInWithIdToken directly.
 *
 * Resolves on success; throws OAuthCancelledError when the user dismisses
 * the provider sheet, and any other error otherwise (Sentry-reported by
 * the caller).
 */
export async function signInWithOAuthProvider({
  provider,
}: {
  provider: OAuthProvider
}): Promise<string | undefined> {
  const credential = await collectCredential(provider)
  return exchangeForSession({ credential, provider })
}

async function collectCredential(
  provider: OAuthProvider,
): Promise<ProviderCredential> {
  if (provider === 'apple') return signInWithApple()
  return signInWithGoogle()
}

async function exchangeForSession({
  credential,
  provider,
}: {
  credential: ProviderCredential
  provider: OAuthProvider
}): Promise<string | undefined> {
  const response = await supabase.functions.invoke('oauth-exchange', {
    body: {
      appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
      displayName: credential.displayName,
      idToken: credential.idToken,
      nonce: credential.nonce,
      platform: 'web',
      provider,
    },
  })

  if (response.error) throw response.error

  const data = response.data as {
    email?: string
    tokenHash?: string
  } | null

  if (!data?.email || !data?.tokenHash) {
    throw new Error('OAuth exchange returned an empty response')
  }

  const { data: verifyData, error } = await supabase.auth.verifyOtp({
    token_hash: data.tokenHash,
    type: 'magiclink',
  })
  if (error) throw error

  const userId = verifyData.user?.id
  if (userId) {
    setUserId(userId)
  }

  return userId
}

export { OAuthCancelledError }
export type { OAuthProvider }
